import type { SiteLayer } from '../../types/site';

export const SITE_LAYER_LEGACY_MIRROR_MAX_BYTES = 256 * 1024;
/** Conservative UTF-8 JSON allowance below Firestore's document ceiling. */
export const SITE_LAYER_PARENT_MAX_BYTES = 950_000;

export interface SiteLayerCompatibilityMirror {
  siteLayers: SiteLayer[];
  siteLayerCount: number;
  siteLayersMirrorComplete: boolean;
}

function serializedBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object'
    && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
}

/** Model set({merge:true}) map merging; arrays remain replacement values. */
function mergedParent(
  live: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...live };
  for (const [key, value] of Object.entries(patch)) {
    result[key] = isPlainRecord(value) && Object.keys(value).length > 0 && isPlainRecord(result[key])
      ? mergedParent(result[key], value)
      : value;
  }
  return result;
}

/** Check a parent patch whose compatibility mirror is owned by another writer. */
export function assertSiteLayerParentBudget(
  liveSite: Record<string, unknown>,
  parentPatch: Record<string, unknown>,
): void {
  if (serializedBytes(mergedParent(liveSite, parentPatch)) > SITE_LAYER_PARENT_MAX_BYTES) {
    throw new Error('Site record exceeds the safe parent-document budget. Republish the Generation & Impact layout to refresh its bounded layer mirror, or compact other site data before editing layers.');
  }
}

/**
 * Project only the compatibility mirror after the complete canonical layer
 * set has been planned for the same atomic write. Count/revision/marker and
 * publication changes belong to parentPatch, so their bytes are included.
 * Retain every full LandGate row when a broad mirror cannot fit: its complete
 * review history remains the browser SED acknowledgement's CAS authority.
 */
export function projectSiteLayerCompatibilityMirror(params: {
  liveSite: Record<string, unknown>;
  parentPatch: Record<string, unknown>;
  canonicalLayers: readonly SiteLayer[];
  /** Versioned publishers historically mirror only complete non-CAD sets. */
  requireCompleteMirror?: boolean;
}): SiteLayerCompatibilityMirror {
  const { liveSite, parentPatch, canonicalLayers, requireCompleteMirror = false } = params;
  const candidates = canonicalLayers.filter(layer => layer.source !== 'cad-import');
  const projection = (layers: SiteLayer[]): SiteLayerCompatibilityMirror => ({
    siteLayers: layers,
    siteLayerCount: canonicalLayers.length,
    siteLayersMirrorComplete: layers.length === canonicalLayers.length,
  });
  const fits = (mirror: SiteLayerCompatibilityMirror): boolean => (
    serializedBytes(mirror.siteLayers) <= SITE_LAYER_LEGACY_MIRROR_MAX_BYTES
    && serializedBytes(mergedParent(liveSite, { ...parentPatch, ...mirror })) <= SITE_LAYER_PARENT_MAX_BYTES
  );
  const full = projection([...candidates]);
  if ((!requireCompleteMirror || full.siteLayersMirrorComplete) && fits(full)) return full;

  // Never keep only a subset of LandGate rows, shorten their geometry/history,
  // or clear their review state to make a parent write fit.
  const reviews = projection(canonicalLayers.filter(layer => layer.source === 'landgate-import'));
  if (fits(reviews)) return reviews;
  throw new Error('Site record exceeds the safe parent-document budget even with only LandGate review layers; compact other site data before saving layers.');
}
